import { useState, useCallback } from 'react';
import { Box, Flex, Grid, Heading, Icon, Spinner, Text, VStack } from '@chakra-ui/react';
import { motion, useReducedMotion } from 'framer-motion';
import { MdSearchOff } from 'react-icons/md';
import { useLocation } from 'wouter';
import { useRecipes } from '@/lib/recipeService';
import { useHomeSearchParams } from '@/hooks/useHomeSearchParams';
import { useScrollRestore } from '@/hooks/useScrollRestore';
import { homeSearchKey } from '@/utils/homeSearchUrl';
import { saveHomeScroll } from '@/utils/homeScroll';
import { useProgressiveList } from '@/hooks/useProgressiveList';
import { ROUTES } from '@/types';
import BrowseHeader from '@/components/BrowseHeader';
import RecipeCard from '@/components/RecipeCard';
import BottomNavBar from '@/components/BottomNavBar';
import FloatingAIChefButton from '@/components/FloatingAIChefButton';
import ProfileSheet from '@/components/ProfileSheet';

const MotionBox = motion(Box);

export default function RecipesPage() {
  const [, navigate] = useLocation();
  const reduceMotion = useReducedMotion();
  // Search/filter state lives in the URL so Back restores it (search → recipe →
  // back keeps the results) and searches are shareable.
  const {
    searchTerm,
    debouncedSearch,
    selectedTagsByGroup,
    selectedRatings,
    setSearchTerm,
    toggleTag,
    toggleRating,
    clearFilters,
  } = useHomeSearchParams();
  const [filterTrigger, setFilterTrigger] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);
  const openFilters = useCallback(() => setFilterTrigger(n => n + 1), []);

  const selectedTagsFlat = Object.values(selectedTagsByGroup).flat();
  const totalActiveFilters = selectedTagsFlat.length + (selectedRatings.length > 0 ? 1 : 0);
  const effectiveMinRating = selectedRatings.length > 0 ? Math.min(...selectedRatings) : undefined;

  const { recipes: allResults, isLoading, isRefreshing, isError } = useRecipes({
    search: debouncedSearch,
    tagsByGroup: selectedTagsByGroup,
    minRating: effectiveMinRating,
  });

  const { visibleItems: visibleRecipes, sentinelRef } = useProgressiveList(allResults, {
    pageSize: 50,
    // Reset on filter params, not the array reference — avoids spurious resets
    // when the store upserts a recipe (e.g. after a rating change mid-scroll).
    resetKey: `${debouncedSearch}|${JSON.stringify(selectedTagsByGroup)}|${effectiveMinRating ?? ''}`,
  });

  const scrollKey = homeSearchKey({ searchTerm: debouncedSearch, selectedTagsByGroup, selectedRatings });
  useScrollRestore(scrollKey, !isLoading && visibleRecipes.length > 0);

  const handleRecipeClick = (recipeId: string) => {
    saveHomeScroll(scrollKey);
    navigate(ROUTES.RECIPE(recipeId));
  };

  return (
    <Box minH="100vh" bg="bg.canvas" pb={{ base: '80px', lg: 0 }}>
      <BrowseHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showFilter
        selectedTags={selectedTagsFlat}
        onToggleTag={toggleTag}
        selectedRatings={selectedRatings}
        onToggleRating={toggleRating}
        onClearFilters={clearFilters}
        totalActiveFilters={totalActiveFilters}
        filterTrigger={filterTrigger}
        onFilterClick={openFilters}
        onProfileClick={() => setProfileOpen(true)}
      />

      {isRefreshing && (
        <Box h="2px" bg="bg.muted" overflow="hidden">
          <Box
            h="full"
            bg="accent.secondary"
            w="40%"
            animation="refreshSlide 1.2s ease-in-out infinite"
            style={{ animationName: 'refreshSlide' }}
            css={{
              '@keyframes refreshSlide': {
                '0%': { transform: 'translateX(-100%)' },
                '100%': { transform: 'translateX(350%)' },
              },
            }}
          />
        </Box>
      )}

      <Box px={{ base: 4, lg: 8 }} pt={3}>
        {isLoading ? (
          <Flex justify="center" py={10}>
            <Spinner size="lg" color="accent.secondary" />
          </Flex>
        ) : isError ? (
          <Text color="text.muted" textAlign="center" py={10}>
            Something went wrong. Try again.
          </Text>
        ) : visibleRecipes.length > 0 ? (
          <MotionBox
            {...(reduceMotion
              ? {}
              : { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.15, ease: 'easeOut' } })}
          >
            <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={2}>
              {visibleRecipes.map(recipe => (
                <RecipeCard key={recipe.id} recipe={recipe} onRecipeClick={handleRecipeClick} />
              ))}
            </Grid>
            <Text
              fontFamily="body"
              fontWeight="300"
              fontSize="12px"
              color="text.muted"
              textAlign="center"
              pt={4}
              pb={2}
            >
              {visibleRecipes.length < allResults.length
                ? `Showing ${visibleRecipes.length} of ${allResults.length} recipes`
                : `${allResults.length} recipe${allResults.length === 1 ? '' : 's'}`}
            </Text>
            <Box ref={sentinelRef} h="1px" />
          </MotionBox>
        ) : (
          <VStack py={16} gap={3} textAlign="center">
            <Box
              w="52px"
              h="52px"
              borderRadius="14px"
              bg="bg.muted"
              display="flex"
              alignItems="center"
              justifyContent="center"
              mx="auto"
            >
              <Icon as={MdSearchOff} boxSize={6} color="text.muted" />
            </Box>
            <Heading fontFamily="heading" fontWeight="800" fontSize="16px" color="text.primary">
              No results
            </Heading>
            <Text
              fontFamily="body"
              fontWeight="300"
              fontSize="13px"
              color="text.muted"
              lineHeight="1.6"
            >
              {debouncedSearch
                ? `Nothing matched "${debouncedSearch}". Try a different word.`
                : 'No recipes match the current filters.'}
            </Text>
          </VStack>
        )}
      </Box>

      <ProfileSheet isOpen={profileOpen} onClose={() => setProfileOpen(false)} />
      <BottomNavBar />
      <FloatingAIChefButton />
    </Box>
  );
}
